/**
 * ══════════════════════════════════════════════════════════════════════════
 *  Usuarios y roles de la marca (#45, endpoints de #70).
 *  Sigue siendo la VISTA DE REFERENCIA del andamio: usa las seis
 *  convenciones una vez cada una (ver `web/dashboard/README.md`).
 * ══════════════════════════════════════════════════════════════════════════
 *
 *   · tipo de la respuesta derivado del OpenAPI, no escrito a mano  → `./users`
 *   · clave de cache declarada en un solo lugar                     → `@/api/keys`
 *   · datos por TanStack Query, nunca `useEffect` + `fetch`         → `useKeysetQuery`
 *   · paginación por cursor, "cargar más" y nunca `OFFSET`          → `hasMore` / `loadMore`
 *   · carga, error y vacío resueltos por los componentes de estado  → `@/ui/states`
 *   · fechas con el locale y el huso del TENANT                     → `fmt.dateTime`
 *   · guarda de rol declarada en la ruta, no acá                    → `routes.tsx`
 *
 * Lo que esta pantalla decide, y por qué
 * --------------------------------------
 * **La contraseña la genera el servidor.** Ni el alta ni el reseteo tienen
 * campo de contraseña: la API devuelve una temporal una única vez y esta vista
 * la muestra en un aviso que dice que no se vuelve a mostrar.
 *
 * **La fila propia no tiene acciones.** Nadie cambia su propio rol ni se da de
 * baja a sí mismo — la API lo rechaza con 403, y un botón que sólo puede
 * terminar en 403 es peor que no tener el botón. La regla es simétrica: un
 * admin que se auto-degrada por error queda sin poder revertirlo.
 *
 * **Una cuenta federada no muestra "restablecer contraseña".** Su credencial
 * vive en el directorio del cliente; XYVONA administra su rol, no su clave.
 *
 * **Todo lo sensible pasa por `ConfirmDialog`.** Cambio de rol, baja,
 * reactivación y reseteo cambian quién puede hacer qué, y los cuatro botones
 * están a un click de distancia entre sí.
 *
 * Lo que esta vista NO hace: mostrar el `unit_id` interno de nada, ni pedir el
 * `tenant_id` en ninguna llamada. El tenant sale del claim `tid` del JWT y lo
 * aplica RLS del lado del servidor.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { ApiError } from '@/api/errors';
import { useKeysetQuery } from '@/api/hooks';
import { userKeys } from '@/api/keys';
import type { Role } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useAuth } from '@/auth/useAuth';
import { ROLE_LABELS } from '@/auth/roles';
import { describeApiError } from '@/i18n/errorMessages';
import type { Translate } from '@/i18n/translate';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { SelectField, TextField } from '../products/ui/fields';
import { AdminNav } from './AdminNav';
import { ConfirmDialog } from './ConfirmDialog';
import { UserCreateDialog } from './UserCreateDialog';
import {
  ASSIGNABLE_ROLES,
  AUTH_MODE_LABEL_KEYS,
  EMPTY_FILTERS,
  isRole,
  type PasswordReset,
  type UserAccount,
  type UserCreated,
  type UserDraft,
  type UserFilters,
} from './users';

const PAGE_SIZE = 25;

/** Acción sensible esperando confirmación. */
type Pending =
  | { kind: 'role'; user: UserAccount; role: Role }
  | { kind: 'deactivate'; user: UserAccount }
  | { kind: 'reactivate'; user: UserAccount }
  | { kind: 'reset'; user: UserAccount };

/** Contraseña temporal recién emitida. Se muestra una vez y se descarta. */
interface Secret {
  email: string;
  password: string;
  /** Sesiones cerradas por el reseteo. `null` en un alta. */
  sessionsRevoked: number | null;
}

export function UsersPage(): ReactNode {
  const { t } = useI18n();
  const { claims } = useAuth();
  const api = useApi();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<UserFilters>(EMPTY_FILTERS);
  const [creating, setCreating] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [secret, setSecret] = useState<Secret | null>(null);

  const query = filters.q.trim() === '' ? null : filters.q.trim();
  const users = useKeysetQuery<UserAccount>(
    userKeys.list({
      limit: PAGE_SIZE,
      role: filters.role,
      active: filters.active,
      q: query,
    }),
    '/api/v1/users',
    { role: filters.role, active: filters.active, q: query },
    { limit: PAGE_SIZE },
  );

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: userKeys.all });
  };

  const create = useMutation<UserCreated, ApiError, UserDraft>({
    mutationFn: (draft) => api.post<UserCreated>('/api/v1/users', draft),
    onSuccess: (created) => {
      setCreating(false);
      if (created.temporary_password !== null) {
        setSecret({
          email: created.user.email,
          password: created.temporary_password,
          sessionsRevoked: null,
        });
      }
      refresh();
    },
  });

  const changeRole = useMutation<UserAccount, ApiError, { id: number; role: Role }>({
    mutationFn: ({ id, role }) => api.patch<UserAccount>(`/api/v1/users/${id}`, { role }),
    onSuccess: () => {
      setPending(null);
      refresh();
    },
  });

  // La baja devuelve 204 sin cuerpo: `undefined` y no `void`, que como
  // parámetro de tipo el linter (con razón) no acepta.
  const deactivate = useMutation<undefined, ApiError, number>({
    mutationFn: (id) => api.delete<undefined>(`/api/v1/users/${id}`),
    onSuccess: () => {
      setPending(null);
      refresh();
    },
  });

  const reactivate = useMutation<UserAccount, ApiError, number>({
    mutationFn: (id) => api.patch<UserAccount>(`/api/v1/users/${id}`, { is_active: true }),
    onSuccess: () => {
      setPending(null);
      refresh();
    },
  });

  const resetPassword = useMutation<PasswordReset, ApiError, UserAccount>({
    mutationFn: (user) => api.post<PasswordReset>(`/api/v1/users/${user.id}/password`, {}),
    onSuccess: (result, user) => {
      setPending(null);
      if (result.temporary_password !== null) {
        setSecret({
          email: user.email,
          password: result.temporary_password,
          sessionsRevoked: result.sessions_revoked,
        });
      }
      refresh();
    },
  });

  const busy =
    changeRole.isPending ||
    deactivate.isPending ||
    reactivate.isPending ||
    resetPassword.isPending;
  const actionError: ApiError | null =
    changeRole.error ?? deactivate.error ?? reactivate.error ?? resetPassword.error;

  const confirmPending = (): void => {
    if (pending === null) return;
    switch (pending.kind) {
      case 'role':
        changeRole.mutate({ id: pending.user.id, role: pending.role });
        return;
      case 'deactivate':
        deactivate.mutate(pending.user.id);
        return;
      case 'reactivate':
        reactivate.mutate(pending.user.id);
        return;
      case 'reset':
        resetPassword.mutate(pending.user);
    }
  };

  const { query: listQuery } = users;
  const filtered =
    filters.role !== null || filters.active !== null || query !== null;

  return (
    <div className="mx-auto max-w-6xl">
      <AdminNav />

      {secret !== null && (
        <TemporaryPassword secret={secret} onDismiss={() => { setSecret(null); }} />
      )}

      <UserFiltersCard filters={filters} onChange={setFilters} />

      <Card className="mt-4">
        <CardHeader
          title={t('users.title')}
          description={t('users.description')}
          actions={
            <>
              {users.items.length > 0 && (
                <Badge tone="neutral">{t('users.count', { count: users.items.length })}</Badge>
              )}
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  create.reset();
                  setCreating(true);
                }}
              >
                {t('users.new')}
              </Button>
            </>
          }
        />

        {actionError !== null && (
          <CardBody className="border-b border-border">
            <p role="alert" className="text-sm font-medium text-critical">
              {describeApiError(actionError, t).title}
            </p>
          </CardBody>
        )}

        {/* Una lista paginada no usa `QueryBoundary` tal cual: hay que
            distinguir "cargando la primera página" de "cargando la
            siguiente". El resto de los estados son los mismos componentes. */}
        {listQuery.isPending && <LoadingState rows={5} />}

        {listQuery.isError && (
          <CardBody>
            <ErrorState
              error={listQuery.error}
              onRetry={() => {
                void listQuery.refetch();
              }}
            />
          </CardBody>
        )}

        {!listQuery.isPending && !listQuery.isError && users.items.length === 0 && (
          <EmptyState title={filtered ? t('users.filter.empty') : t('users.empty')} />
        )}

        {users.items.length > 0 && (
          <>
            <Table caption={t('users.description')}>
              <thead>
                <tr>
                  <Th>{t('users.column.name')}</Th>
                  <Th>{t('users.column.email')}</Th>
                  <Th>{t('users.column.role')}</Th>
                  <Th>{t('users.column.authMode')}</Th>
                  <Th>{t('users.column.status')}</Th>
                  <Th>{t('users.column.lastLogin')}</Th>
                  <Th>{t('users.column.actions')}</Th>
                </tr>
              </thead>
              <tbody>
                {users.items.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    isSelf={claims?.userId === user.id}
                    busy={busy}
                    onAction={setPending}
                  />
                ))}
              </tbody>
            </Table>

            <CardBody className="flex items-center justify-center border-t border-border">
              {users.hasMore ? (
                <Button size="sm" onClick={users.loadMore} disabled={users.isLoadingMore}>
                  {users.isLoadingMore ? t('state.loadingMore') : t('state.loadMore')}
                </Button>
              ) : (
                <p className="text-xs text-ink-subtle">{t('state.allLoaded')}</p>
              )}
            </CardBody>
          </>
        )}
      </Card>

      <RolesCard />

      <UserCreateDialog
        open={creating}
        busy={create.isPending}
        error={create.error}
        onCancel={() => {
          setCreating(false);
        }}
        onSubmit={(draft) => {
          create.mutate(draft);
        }}
      />

      <ConfirmDialog
        open={pending !== null}
        title={pending === null ? '' : t(CONFIRM_TITLES[pending.kind])}
        detail={pending === null ? '' : confirmDetail(pending, t)}
        {...(pending === null ? {} : { confirmLabel: t(CONFIRM_LABELS[pending.kind]) })}
        busy={busy}
        onCancel={() => {
          setPending(null);
        }}
        onConfirm={confirmPending}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────── filas

interface UserRowProps {
  user: UserAccount;
  isSelf: boolean;
  busy: boolean;
  onAction: (pending: Pending) => void;
}

function UserRow({ user, isSelf, busy, onAction }: UserRowProps): ReactNode {
  const { t, fmt } = useI18n();
  // Una respuesta sin `auth_mode` (una API más vieja que este dashboard) se
  // lee como cuenta local: es el caso que existe hoy en todos los tenants.
  const federated = user.auth_mode === 'oidc';
  const authMode = federated ? 'oidc' : 'password';

  return (
    <tr>
      <Td className="font-medium">{user.full_name ?? '—'}</Td>
      <Td className="text-ink-muted">{user.email}</Td>
      <Td>
        {isSelf ? (
          // Nadie cambia su propio rol: la API lo rechaza con 403 y el
          // desplegable sólo serviría para llegar a ese error.
          <Badge tone="neutral">{ROLE_LABELS[user.role]}</Badge>
        ) : (
          <select
            aria-label={t('users.action.changeRole', { email: user.email })}
            value={user.role}
            disabled={busy || !user.is_active}
            className="rounded border border-border-strong bg-surface px-2 py-1 text-sm text-ink disabled:text-ink-subtle"
            onChange={(event) => {
              const value = event.target.value;
              if (isRole(value) && value !== user.role) {
                onAction({ kind: 'role', user, role: value });
              }
            }}
          >
            {ASSIGNABLE_ROLES.map((role) => (
              <option key={role} value={role}>
                {ROLE_LABELS[role]}
              </option>
            ))}
          </select>
        )}
      </Td>
      <Td>
        <Badge tone="neutral">{t(AUTH_MODE_LABEL_KEYS[authMode])}</Badge>
      </Td>
      <Td>
        <Badge tone={user.is_active ? 'verified' : 'neutral'}>
          {user.is_active ? t('users.status.active') : t('users.status.inactive')}
        </Badge>
      </Td>
      <Td className="text-ink-muted">
        {user.last_login_at === null || user.last_login_at === undefined
          ? t('users.never')
          : fmt.dateTime(user.last_login_at)}
      </Td>
      <Td>
        {isSelf ? (
          <span className="text-xs text-ink-subtle">{t('users.action.self')}</span>
        ) : (
          <div className="flex flex-wrap gap-2">
            {user.is_active ? (
              <Button
                size="sm"
                variant="critical"
                disabled={busy}
                onClick={() => {
                  onAction({ kind: 'deactivate', user });
                }}
              >
                {t('users.action.deactivate')}
              </Button>
            ) : (
              <Button
                size="sm"
                disabled={busy}
                onClick={() => {
                  onAction({ kind: 'reactivate', user });
                }}
              >
                {t('users.action.reactivate')}
              </Button>
            )}
            {federated ? (
              <span className="text-xs text-ink-subtle">{t('users.action.federated')}</span>
            ) : (
              <Button
                size="sm"
                disabled={busy}
                onClick={() => {
                  onAction({ kind: 'reset', user });
                }}
              >
                {t('users.action.reset')}
              </Button>
            )}
          </div>
        )}
      </Td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────── filtros

interface UserFiltersCardProps {
  filters: UserFilters;
  onChange: (filters: UserFilters) => void;
}

/**
 * Rol, estado y búsqueda. Los tres los resuelve la API (`?role=&active=&q=`):
 * filtrar del lado del cliente sólo alcanzaría a la página ya traída, así que
 * con doscientos usuarios mostraría resultados incompletos sin decirlo.
 */
function UserFiltersCard({ filters, onChange }: UserFiltersCardProps): ReactNode {
  const { t } = useI18n();
  const [text, setText] = useState(filters.q);

  return (
    <Card className="mt-4">
      <CardHeader title={t('users.filter.title')} description={t('users.filter.description')} />
      <CardBody className="grid gap-4 sm:grid-cols-3">
        <form
          onSubmit={(event) => {
            // Se busca al enviar y no en cada tecla: una consulta por
            // pulsación sobre una tabla con RLS es tráfico que no compra nada.
            event.preventDefault();
            onChange({ ...filters, q: text });
          }}
        >
          <TextField
            label={t('users.filter.search')}
            value={text}
            onChange={setText}
            hint={t('users.filter.searchHint')}
            placeholder={t('users.filter.searchPlaceholder')}
          />
          <button type="submit" className="sr-only">
            {t('users.filter.searchAction')}
          </button>
        </form>

        <SelectField
          label={t('users.filter.role')}
          value={filters.role ?? ''}
          onChange={(value) => {
            onChange({ ...filters, role: isRole(value) ? value : null });
          }}
          options={[
            { value: '', label: t('users.filter.allRoles') },
            ...ASSIGNABLE_ROLES.map((role) => ({ value: role, label: ROLE_LABELS[role] })),
          ]}
        />

        <SelectField
          label={t('users.filter.status')}
          value={filters.active === null ? '' : String(filters.active)}
          onChange={(value) => {
            onChange({ ...filters, active: value === '' ? null : value === 'true' });
          }}
          options={[
            { value: '', label: t('users.filter.allStatuses') },
            { value: 'true', label: t('users.status.active') },
            { value: 'false', label: t('users.status.inactive') },
          ]}
        />
      </CardBody>
    </Card>
  );
}

// ──────────────────────────────────────────────── contraseña temporal

/**
 * La temporal se muestra **una sola vez**: no queda guardada en claro en
 * ningún lado y no se puede volver a consultar. Decirlo en pantalla es lo que
 * evita el ticket de soporte "¿me la reenvían?".
 */
function TemporaryPassword({
  secret,
  onDismiss,
}: {
  secret: Secret;
  onDismiss: () => void;
}): ReactNode {
  const { t } = useI18n();

  return (
    <Card className="mt-4 border-accent">
      <CardBody role="status" className="space-y-2">
        <h2 className="text-sm font-semibold text-ink">{t('users.temp.title')}</h2>
        <p className="text-sm text-ink-muted">
          {t('users.temp.detail', { email: secret.email })}
        </p>
        <p className="rounded border border-border-strong bg-surface-sunken px-3 py-2 font-mono text-sm text-ink">
          {secret.password}
        </p>
        {secret.sessionsRevoked !== null && (
          <p className="text-xs text-ink-subtle">
            {t('users.temp.sessions', { count: secret.sessionsRevoked })}
          </p>
        )}
        <Button size="sm" onClick={onDismiss}>
          {t('users.temp.dismiss')}
        </Button>
      </CardBody>
    </Card>
  );
}

// ─────────────────────────────────────────────────────── confirmaciones

const CONFIRM_TITLES = {
  role: 'users.confirm.role.title',
  deactivate: 'users.confirm.deactivate.title',
  reactivate: 'users.confirm.reactivate.title',
  reset: 'users.confirm.reset.title',
} as const;

const CONFIRM_LABELS = {
  role: 'users.confirm.role.accept',
  deactivate: 'users.confirm.deactivate.accept',
  reactivate: 'users.confirm.reactivate.accept',
  reset: 'users.confirm.reset.accept',
} as const;

function confirmDetail(pending: Pending, t: Translate): string {
  const who = pending.user.full_name ?? pending.user.email;
  switch (pending.kind) {
    case 'role':
      return t('users.confirm.role.detail', { who, role: ROLE_LABELS[pending.role] });
    case 'deactivate':
      return t('users.confirm.deactivate.detail', { who });
    case 'reactivate':
      return t('users.confirm.reactivate.detail', { who });
    case 'reset':
      return t('users.confirm.reset.detail', { who });
  }
}

// ───────────────────────────────────────────────────────── qué puede cada rol

/**
 * Qué puede hacer cada rol, y dónde se ve quién lo cambió.
 *
 * La jerarquía (`auth/roles.ts`) es lo primero que pregunta un administrador
 * nuevo, y la respuesta no está en ninguna otra pantalla. El enlace a la
 * auditoría filtrada por `api_user` es el que contesta la segunda pregunta —
 * "¿quién le dio admin a esta persona?" —, que es la que aparece en una
 * inspección.
 */
function RolesCard(): ReactNode {
  const { t } = useI18n();

  return (
    <Card className="mt-4">
      <CardHeader title={t('users.roles.title')} />
      <CardBody className="space-y-3">
        <dl className="space-y-2 text-sm">
          {(
            [
              ['admin', t('users.roles.admin')],
              ['analyst', t('users.roles.analyst')],
              ['viewer', t('users.roles.viewer')],
            ] as const
          ).map(([role, detail]) => (
            <div key={role} className="flex flex-wrap items-baseline gap-2">
              <dt>
                <Badge tone="neutral">{ROLE_LABELS[role]}</Badge>
              </dt>
              <dd className="text-ink-muted">{detail}</dd>
            </div>
          ))}
        </dl>
        <p className="max-w-prose text-sm text-ink-muted">{t('users.roles.audited')}</p>
        <Link
          to="/admin/audit"
          className="inline-block text-sm font-medium text-accent underline-offset-2 hover:underline"
        >
          {t('users.roles.seeAudit')}
        </Link>
      </CardBody>
    </Card>
  );
}
